// Account → Plan (BL-132): the mockup «Account», tweak `plan` (ADR-0031), one card per state of ADR-0045. The plan is
// ours, the money is Stripe's (ADR-0046): Subscribe → /subscribe, Cancel / Resume / Switch → POST /api/billing/*, the
// card → the customer portal. Every action is «pending» until the webhook lands - the card polls the state meanwhile.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { ApiError, type BillingState } from "../api";
import {
  ACTION_WAIT_MS,
  CHECKOUT_SYNC_AFTER_MS,
  CHECKOUT_WAIT_MS,
  POLL_MS,
  billingErrorText,
  redeemErrorText,
  redeemGiftCode,
  cancelPlan,
  daysText,
  planPrices,
  resumePlan,
  sleep,
  subscriptionEnded,
  switchPlan,
  syncCheckout,
  useBillingState,
  usePortal,
} from "../billing";
import { t } from "../i18n";
import { keepsGiftDays, keepsTrialDays, longDay } from "./SubscribePage";
import { BillingCard } from "./BillingCard";

/** A button that opens the Stripe customer portal (card, open invoice); a refusal is one line under it. */
export function UpdateCardButton({ label, className = "btn btn-primary" }: { label: string; className?: string }) {
  const portal = usePortal();
  return (
    <>
      <button type="button" className={className} disabled={portal.busy} onClick={() => void portal.open()} data-testid="update-card">
        {label}
      </button>
      {portal.error && <span className="plan-error" role="alert">{portal.error}</span>}
    </>
  );
}

/** /preview answered 402 (ADR-0045: the page reads sources, so it is blocked like the tools): the message and one button. */
export function PreviewPaywall({ error }: { error: ApiError }) {
  const paused = error.code === "payment_paused";
  return (
    <section className="pv-card plan-paywall" data-testid="preview-paywall">
      <h2>{t(paused ? "plan.paywall.paused" : "plan.paywall.required")}</h2>
      <p className="pv-lead">{error.detail || t("plan.paywall.fallback")}</p>
      <p className="plan-paywall-note">{t("plan.paywall.nothingRead")}</p>
      <div className="plan-actions">
        {paused ? (
          <UpdateCardButton label={t("plan.updateCard")} />
        ) : (
          <Link className="btn btn-primary" to="/subscribe">
            {t("plan.subscribe")}
          </Link>
        )}
      </div>
    </section>
  );
}

type Primary =
  | { kind: "subscribe"; label: string }
  | { kind: "portal"; label: string }
  | { kind: "resume"; label: string }
  | { kind: "switch"; label: string; to: "monthly" | "yearly" };

interface View {
  tag: string;
  quiet: boolean;
  headline: string;
  body: string[];
  price: { amount: string; note: string } | null;
  progress: { pct: number; left: string; right: string } | null;
  primary: Primary | null;
  secondary: { label: string; to: string } | null;
  canCancel: boolean;
}

/** The amounts of the list (EUR) match what the buyer pays: the last payment was in EUR, or there was none yet. */
const listCurrency = (billing: BillingState): boolean => {
  const c = billing.last_payment_currency;
  return !c || c.toUpperCase() === (billing.plan.price?.currency ?? "EUR").toUpperCase();
};

/** Texts and actions of one state - the mockup's, rewritten where the facts differ (CHANGELOG-design, BL-132). */
function viewOf(billing: BillingState): View {
  const plan = billing.plan;
  const prices = planPrices(plan);
  const yearly = plan.period === "yearly";
  const price = { amount: yearly ? prices.yearly : prices.monthly, note: yearly ? t("plan.noteYearly") : t("plan.noteMonthly", { yearly: prices.yearly }) };
  const day = (d: string | null | undefined) => (d ? longDay(d) : "");
  const ends = day(plan.ends_on);
  const periodEnd = day(plan.period_end ?? plan.ends_on);
  const left = daysText(plan.days_left ?? 0);
  const included = { label: t("plan.whatsIncluded"), to: "/pricing" };
  const invoices = billing.has_billing_account ? { label: t("plan.invoices"), to: "#billing" } : null;
  const base = { price, progress: null, secondary: invoices, canCancel: false, quiet: false };

  // Trial progress against `trial_days` (30, +3 after the extension - ADR-0045; those who existed at launch get a gift instead, ADR-0059);
  // an older server without it: 30 days, or 60 when more than 30 are left.
  const progress = () => {
    const daysLeft = plan.days_left ?? 0;
    const total = plan.trial_days && plan.trial_days > 0 ? plan.trial_days : daysLeft > 30 ? 60 : 30;
    return {
      pct: Math.min(100, Math.max(0, ((total - daysLeft) / total) * 100)),
      left: plan.trial_started_on ? t("plan.progressStarted", { date: longDay(plan.trial_started_on) }) : t("plan.progressLeft", { left }),
      right: t("plan.progressEnds", { date: ends }),
    };
  };

  switch (plan.state) {
    case "trial":
      return {
        ...base,
        tag: t("plan.tag.trial"),
        headline: plan.trial_days ? t("plan.trial.headlineDays", { total: plan.trial_days, left: plan.days_left ?? 0 }) : t("plan.trial.headline", { left }),
        body: [t("plan.trial.body", { date: ends })],
        progress: progress(),
        primary: { kind: "subscribe", label: t("plan.subscribe") },
        secondary: included,
      };
    case "trial-ending": {
      const keep = keepsTrialDays(plan);
      return {
        ...base,
        tag: t("plan.tag.trialEnding", { left }),
        headline: t("plan.ending.headline", { date: ends }),
        body: [t("plan.ending.body"), ...(keep ? [t("plan.ending.keep", { date: ends })] : [])],
        progress: progress(),
        primary: { kind: "subscribe", label: t(keep ? "plan.subscribeFree" : "plan.subscribe") },
        secondary: included,
      };
    }
    case "expired": {
      const ended = subscriptionEnded(plan);
      return {
        ...base,
        quiet: true,
        tag: t(ended ? "plan.tag.ended" : "plan.tag.expired"),
        headline: t(ended ? "plan.ended.headline" : "plan.expired.headline", { date: ends }),
        body: [t("plan.expired.body"), ...(plan.extension?.available ? [t("plan.expired.extension")] : [])],
        price: { amount: prices.monthly, note: t("plan.noteMonthly", { yearly: prices.yearly }) },
        primary: { kind: "subscribe", label: t("plan.subscribeMonthly", { price: prices.monthly }) },
        secondary: ended && invoices ? invoices : included,
      };
    }
    case "active": {
      // BL-177: the list price is in euros; a buyer charged in another currency (Adaptive Pricing) sees the amount on
      // Stripe's invoice, so the euro sum is shown only when the last payment was in EUR (or none is known yet).
      const inList = listCurrency(billing);
      return {
        ...base,
        tag: t(yearly ? "plan.tag.yearly" : plan.period === "monthly" ? "plan.tag.monthly" : "plan.tag.active"),
        headline: inList ? t(yearly ? "plan.yearly.headline" : "plan.monthly.headline", { date: periodEnd, price: price.amount }) : t("plan.renews", { date: periodEnd }),
        body: [
          yearly ? t("plan.yearly.body") : inList ? t("plan.monthly.body", { yearly: prices.yearly }) : t("plan.monthly.bodyOther"),
          // ADR-0058: Stripe sells the new subscriptions (tax included); the older ones keep VALAS and prices without VAT.
          billing.managed ? t("plan.seller") : t("plan.vatExcluded"),
        ],
        primary: yearly
          ? { kind: "switch", label: t("plan.toMonthly"), to: "monthly" }
          : plan.period === "monthly"
            ? { kind: "switch", label: t(inList ? "plan.toYearly" : "plan.toYearlyFree"), to: "yearly" }
            : null,
        canCancel: true,
      };
    }
    case "past-due": {
      const pause = day(plan.pause_on);
      return {
        ...base,
        tag: t("plan.tag.pastDue"),
        headline: t("plan.pastDue.headline"),
        body: [pause ? t("plan.pastDue.body", { date: pause }) : t("plan.pastDue.bodyNoDate")],
        primary: { kind: "portal", label: t("plan.updatePayment") },
        canCancel: true,
      };
    }
    case "paused":
      return {
        ...base,
        quiet: true,
        tag: t("plan.tag.paused"),
        headline: t("plan.paused.headline"),
        body: [t("plan.paused.body")],
        primary: { kind: "portal", label: t("plan.updateCard") },
      };
    case "cancelled":
      return {
        ...base,
        quiet: true,
        tag: t("plan.tag.cancelled"),
        headline: t("plan.cancelled.headline", { date: periodEnd }),
        body: [t("plan.cancelled.body", { date: periodEnd })],
        primary: { kind: "resume", label: t("plan.resume") },
      };
    case "gift": {
      // ADR-0059: full access until the gift ends; Subscribe starts the paid plan on that day (or at once with < 48 h left).
      const keep = keepsGiftDays(plan);
      return {
        ...base,
        tag: plan.gift_ending ? t("plan.tag.giftEnding", { left }) : t("plan.tag.gift"),
        headline: t("plan.gift.headline", { date: ends }),
        body: [t("plan.gift.body", { date: ends }), t(keep ? "plan.gift.startsOn" : "plan.gift.startsNow", { date: ends })],
        primary: { kind: "subscribe", label: keep ? t("plan.gift.subscribe", { date: ends }) : t("plan.gift.subscribeNow", { price: prices.monthly }) },
        secondary: included,
      };
    }
    default:
      // comp (and anything unknown): access without a plan to manage - no Subscribe, no price.
      return {
        ...base,
        tag: t("plan.tag.comp"),
        headline: t("plan.comp.headline"),
        body: [t("plan.comp.body"), ...(plan.comp_until ? [t("plan.comp.until", { date: longDay(plan.comp_until) })] : [])],
        price: null,
        primary: null,
        secondary: null,
      };
  }
}

/** States in which a gift code can be redeemed (ADR-0059): no subscription and no gift in force. */
const CAN_REDEEM = ["trial", "trial-ending", "expired"];

/** «Have a gift code?» (ADR-0059): one field and a button; every refusal of a wrong code reads the same. */
function GiftCodeForm({ reload }: { reload: () => Promise<BillingState | null> }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) {
      setError(t("plan.gift.empty"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await redeemGiftCode(code.trim());
      if (!alive.current) return;
      setCode("");
      // The card turns into «Gift subscription until …» once the state is reloaded; this form goes away with it.
      await reload();
    } catch (err) {
      if (!alive.current) return;
      setError(redeemErrorText(err));
      // A refusal that depends on the state (a gift already running, a subscription): show the real state.
      if (err instanceof ApiError && err.status === 409) await reload();
    }
    if (alive.current) setBusy(false);
  }

  return (
    <form className="plan-gift" onSubmit={(e) => void submit(e)} data-testid="gift-form">
      <label className="plan-label" htmlFor="gift-code">
        {t("plan.gift.label")}
      </label>
      <div className="plan-gift-row">
        <input
          id="gift-code"
          className="input"
          value={code}
          placeholder={t("plan.gift.placeholder")}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={40}
          onChange={(e) => setCode(e.target.value)}
          data-testid="gift-input"
        />
        <button type="submit" className="btn btn-secondary" disabled={busy} data-testid="gift-redeem">
          {busy ? t("plan.gift.redeeming") : t("plan.gift.redeem")}
        </button>
      </div>
      <p className="plan-body">{t("plan.gift.hint")}</p>
      {error && (
        <p className="plan-error" role="alert" data-testid="gift-error">
          {error}
        </p>
      )}
    </form>
  );
}

type Confirm = "cancel" | "switch" | null;

function PlanCard({ billing, reload }: { billing: BillingState; reload: () => Promise<BillingState | null> }) {
  const view = viewOf(billing);
  const portal = usePortal();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const periodEnd = billing.plan.period_end ?? billing.plan.ends_on;
  const periodDay = periodEnd ? longDay(periodEnd) : "";

  /** The action is accepted; the state changes when Stripe's webhook lands - poll until `done` or give up. */
  async function waitFor(done: (s: BillingState) => boolean) {
    setWaiting(true);
    for (let elapsed = 0; elapsed < ACTION_WAIT_MS; elapsed += POLL_MS) {
      await sleep(POLL_MS);
      if (!alive.current) return;
      const s = await reload();
      if (!alive.current) return;
      if (s && done(s)) {
        setWaiting(false);
        return;
      }
    }
    setWaiting(false);
    setNote(t("plan.slow"));
  }

  async function run(action: () => Promise<unknown>, done: ((s: BillingState) => boolean) | null, after?: string) {
    setConfirm(null);
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await action();
    } catch (e) {
      if (!alive.current) return;
      setBusy(false);
      setError(billingErrorText(e));
      await reload();
      return;
    }
    if (done) await waitFor(done);
    if (!alive.current) return;
    if (after) setNote(after);
    setBusy(false);
  }

  const cancel = () => run(cancelPlan, (s) => s.cancel_at_period_end || s.plan.state === "cancelled");
  const resume = () => run(resumePlan, (s) => !s.cancel_at_period_end && s.plan.state !== "cancelled");
  // Monthly → yearly is immediate (prorated); yearly → monthly is a schedule at renewal - nothing to wait for.
  const doSwitch = (to: "monthly" | "yearly") =>
    to === "yearly"
      ? run(() => switchPlan("yearly"), (s) => s.plan.period === "yearly")
      : run(() => switchPlan("monthly"), null, t("plan.switchedMonthly", { date: periodDay }));

  const p = view.primary;
  let primary: ReactNode = null;
  if (p?.kind === "subscribe") {
    primary = (
      <Link className="btn btn-primary plan-primary" to="/subscribe" data-testid="plan-primary">
        {p.label}
      </Link>
    );
  } else if (p) {
    const onClick = p.kind === "portal" ? () => void portal.open() : p.kind === "resume" ? () => void resume() : () => setConfirm("switch");
    primary = (
      <button type="button" className="btn btn-primary plan-primary" disabled={busy || portal.busy} onClick={onClick} data-testid="plan-primary">
        {p.label}
      </button>
    );
  }

  const prices = planPrices(billing.plan);
  const confirmText =
    confirm === "cancel"
      ? periodDay
        ? t("plan.cancelConfirm", { date: periodDay })
        : t("plan.cancelConfirmNoDate")
      : p?.kind === "switch" && p.to === "yearly"
        ? listCurrency(billing)
          ? t("plan.toYearlyConfirm", { yearly: prices.yearly })
          : t("plan.toYearlyConfirmOther")
        : t("plan.toMonthlyConfirm", { date: periodDay });

  return (
    <section id="plan" className="pv-card plan-card" data-testid="plan-card" data-state={billing.plan.state}>
      <div className="plan-top">
        <div className="plan-copy">
          <div className="plan-title-row">
            <h2>{t("plan.title")}</h2>
            <span className={`tag ${view.quiet ? "plan-tag-quiet" : "tag-accent"}`} data-testid="plan-tag">
              {view.tag}
            </span>
          </div>
          <p className="plan-headline">{view.headline}</p>
          {view.body.map((b) => (
            <p key={b} className="plan-body">
              {b}
            </p>
          ))}
        </div>
        {view.price && (
          <div className="plan-price">
            <span className="plan-label">{t("plan.included")}</span>
            <span className="plan-amount">{view.price.amount}</span>
            <span>{view.price.note}</span>
          </div>
        )}
      </div>
      {view.progress && (
        <div className="plan-progress">
          <div className="plan-bar">
            <div style={{ width: `${view.progress.pct}%` }} />
          </div>
          <div className="plan-bar-labels">
            <span>{view.progress.left}</span>
            <span>{view.progress.right}</span>
          </div>
        </div>
      )}
      {(primary || view.secondary || view.canCancel) && (
        <div className="plan-actions">
          {primary}
          {billing.plan.state === "active" && (
            <button type="button" className="text-btn" disabled={busy || portal.busy} onClick={() => void portal.open("subscription_update")} data-testid="plan-change-portal">
              {t("plan.changePortal")}
            </button>
          )}
          {view.secondary &&
            (view.secondary.to.startsWith("#") ? (
              <a className="plan-secondary" href={view.secondary.to}>
                {view.secondary.label}
              </a>
            ) : (
              <Link className="plan-secondary" to={view.secondary.to}>
                {view.secondary.label}
              </Link>
            ))}
          {confirm ? (
            <span className="plan-confirm" data-testid="plan-confirm">
              <span className="plan-confirm-text">{confirmText}</span>
              <button
                type="button"
                className={confirm === "cancel" ? "btn btn-ghost acct-muted-btn" : "btn btn-primary"}
                disabled={busy}
                onClick={() => void (confirm === "cancel" ? cancel() : p?.kind === "switch" ? doSwitch(p.to) : undefined)}
                data-testid="plan-confirm-yes"
              >
                {t(confirm === "cancel" ? "plan.cancelYes" : "plan.switchYes")}
              </button>
              <button type="button" className={confirm === "cancel" ? "btn btn-secondary" : "btn btn-ghost"} onClick={() => setConfirm(null)} data-testid="plan-keep">
                {t("plan.keep")}
              </button>
            </span>
          ) : (
            view.canCancel && (
              <button type="button" className="btn btn-ghost acct-muted-btn plan-cancel" disabled={busy} onClick={() => setConfirm("cancel")} data-testid="plan-cancel">
                {t("plan.cancel")}
              </button>
            )
          )}
        </div>
      )}
      {CAN_REDEEM.includes(billing.plan.state) && <GiftCodeForm reload={reload} />}
      {waiting && (
        <p className="plan-status" role="status" data-testid="plan-waiting">
          {t("plan.waiting")}
        </p>
      )}
      {note && (
        <p className="plan-status" role="status">
          {note}
        </p>
      )}
      {(error || portal.error) && (
        <p className="plan-error" role="alert">
          {error ?? portal.error}
        </p>
      )}
    </section>
  );
}

type Arrival = "waiting" | "done" | "slow" | null;

/**
 * Plan and Billing on /account. Back from Stripe (`?checkout=success&session_id=…`, ADR-0046) it polls the state every
 * 2 s until `active`; after 10 s without it, one sync call (the server reads the Checkout session), then up to 20 s more.
 */
export function AccountPlan() {
  const { state, reload, set } = useBillingState(true, false);
  const [search] = useSearchParams();
  const { hash } = useLocation();
  const session = search.get("checkout") === "success" ? (search.get("session_id") ?? "") : null;
  const [arrival, setArrival] = useState<Arrival>(session === null ? null : "waiting");

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (session === null) {
        await reload();
        return;
      }
      let synced = false;
      for (let elapsed = 0; elapsed <= CHECKOUT_WAIT_MS; elapsed += POLL_MS) {
        if (elapsed > 0) await sleep(POLL_MS);
        if (!alive) return;
        let s: BillingState | null;
        if (!synced && session && elapsed >= CHECKOUT_SYNC_AFTER_MS) {
          synced = true;
          try {
            const r = await syncCheckout(session);
            s = r?.plan ? r : null;
            if (s && alive) set(s);
          } catch {
            s = await reload();
          }
        } else {
          s = await reload();
        }
        if (!alive) return;
        if (s?.plan.state === "active") {
          setArrival("done");
          return;
        }
      }
      setArrival("slow");
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // /account#plan from the menu, the strip or update_card_url: the card appears after the state loads.
  const loaded = state !== undefined;
  useEffect(() => {
    if (!loaded || (hash !== "#plan" && hash !== "#billing")) return;
    document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: "start" });
  }, [loaded, hash]);

  if (state === undefined) return null;
  if (state === null) return <div className="msg error">{t("plan.loadFailed")}</div>;
  if (state.plan.state === "off") return null;
  return (
    <>
      {arrival && (
        <div className={`plan-arrival${arrival === "done" ? " done" : ""}`} role="status" data-testid="checkout-arrival">
          {arrival === "done" ? (
            <>
              <strong>{t("plan.checkout.done")}</strong>
              <span>{t("plan.checkout.doneText")}</span>
            </>
          ) : (
            <span>{t(arrival === "waiting" ? "plan.checkout.waiting" : "plan.checkout.slow")}</span>
          )}
        </div>
      )}
      <PlanCard billing={state} reload={reload} />
      {state.has_billing_account && <BillingCard />}
    </>
  );
}
